import { Link, Pencil, Plus, Tv, Trash2 } from 'lucide-react';
import { useAuth } from 'modules/auth/ui/hooks/use-auth';
import { DeviceDto } from 'modules/core/supabase/tables/device';
import { TokenStorage } from 'modules/core/token-storage';
import { DevicesNavigationState } from 'modules/devices/domain/constants';
import DeviceForm, { DeviceFormValues } from 'modules/devices/ui/components/device-form/device-form';
import { useCreateDevice } from 'modules/devices/ui/hooks/use-create-device';
import { useDeleteDevice } from 'modules/devices/ui/hooks/use-delete-device';
import { useDevicesList } from 'modules/devices/ui/hooks/use-devices-list';
import { useHidePanelOption } from 'modules/devices/ui/hooks/use-hide-panel-option';
import { useUpdateDevice } from 'modules/devices/ui/hooks/use-update-device';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getNormalizedDate } from 'shared/utils/get-normalized-date';
import { theme } from 'styles/theme/theme';
import { UDButton, UDText } from 'ud-ui/components';
import UDBreadcrumbs from 'ud-ui/components/breadcrumbs';
import EmptyButton from 'ud-ui/components/empty-button';
import ImgNotFound from 'ud-ui/components/images/not-found.png';
import Modal from 'ud-ui/components/modal';
import UDPagination from 'ud-ui/components/pagination';
import UDPerPage from 'ud-ui/components/per-page/per-page';
import { UDTable } from 'ud-ui/components/table';
import UDTippy from 'ud-ui/components/tippy';
import UDCheckbox from 'ud-form/components/checkbox/component';
import { useMainLayout } from 'modules/app-shell/ui/layout/context';
import * as S from './styles';

function DevicesPage() {
  const { setHeaderContent } = useMainLayout();
  const { userTournamentId: tournamentId } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const { devices, isLoading, isError, page, perPage, pages, setPage, setPerPage, refetch } = useDevicesList(tournamentId);

  const { hidePanel, setHidePanel } = useHidePanelOption();
  const toggleHidePanel = useCallback(() => setHidePanel(!hidePanel), [hidePanel, setHidePanel]);

  const [selectedDevice, setSelectedDevice] = useState<DeviceDto | undefined>(undefined);

  const [isOpenForm, setIsOpenForm] = useState(false);
  const openForm = useCallback(() => setIsOpenForm(true), []);
  const closeForm = useCallback(() => setIsOpenForm(false), []);

  const [isOpenDelete, setIsOpenDelete] = useState(false);
  const openDelete = useCallback(() => setIsOpenDelete(true), []);
  const closeDelete = useCallback(() => setIsOpenDelete(false), []);

  // Пришли с кнопки «Ссылка на табло для OBS» в карточке матча — объясняем,
  // куда переехал функционал, и сбрасываем флаг, чтобы окно не всплыло при возврате.
  const [isOpenOverlayNotice, setIsOpenOverlayNotice] = useState(() =>
    Boolean((location.state as DevicesNavigationState)?.fromMatchOverlay),
  );
  const closeOverlayNotice = useCallback(() => {
    setIsOpenOverlayNotice(false);
    navigate(Pages.DEVICES.INDEX, { replace: true, state: null });
  }, [navigate]);

  const openCreateModal = useCallback(() => {
    setSelectedDevice(undefined);
    openForm();
  }, [openForm]);

  const openEditModal = useCallback(
    (device: DeviceDto) => {
      setSelectedDevice({ ...device });
      openForm();
    },
    [openForm],
  );

  const openDeleteModal = useCallback(
    (device: DeviceDto) => {
      setSelectedDevice(device);
      openDelete();
    },
    [openDelete],
  );

  const createDevice = useCreateDevice(tournamentId, refetch);
  const updateDevice = useUpdateDevice(refetch);
  const deleteDevice = useDeleteDevice(refetch);

  const onSubmit = useCallback(
    async (values: DeviceFormValues) => {
      if (selectedDevice) {
        await updateDevice.mutate(selectedDevice.id, { name: values.name });
      } else {
        await createDevice.mutate({ name: values.name });
      }
      closeForm();
    },
    [selectedDevice, createDevice, updateDevice, closeForm],
  );

  const onDelete = useCallback(async () => {
    if (!selectedDevice) return;
    await deleteDevice.mutate(selectedDevice.id);
    closeDelete();
  }, [selectedDevice, deleteDevice, closeDelete]);

  const tokenStorage = useMemo(() => new TokenStorage(), []);

  const copyLink = useCallback((path: string) => {
    navigator.clipboard.writeText(window.location.origin + path).then(() => {
      toast.success('Ссылка скопирована');
    });
  }, []);

  const handleCopyLink = useCallback(
    (device: DeviceDto) => {
      const token = tokenStorage.getToken() ?? '';
      // Без настройки ссылку не засоряем — параметр появляется, только когда его попросили.
      const options = hidePanel ? { hidePanel: true } : undefined;
      copyLink(Pages.OVERLAY_V2.builders.index(String(tournamentId), String(device.id), token, options));
    },
    [copyLink, tournamentId, tokenStorage, hidePanel],
  );

  // Панель управления живёт по тому же устройству, что и оверлей: со счётом, таймом и
  // составами работают из неё, пока оверлей показывается в OBS.
  const handleCopyControlPanelLink = useCallback(
    (device: DeviceDto) => {
      const token = tokenStorage.getToken() ?? '';
      copyLink(Pages.OVERLAY_V2.builders.controlPanel(String(tournamentId), String(device.id), token));
    },
    [copyLink, tournamentId, tokenStorage],
  );

  const headerContent = useMemo(
    () => (
      <div className="flex w-full items-center justify-between">
        <UDBreadcrumbs
          className="my-10"
          breadcrumbs={[
            { label: 'Трансляции', to: Pages.TEMPLATE.INDEX },
            { label: 'Устройства', to: Pages.DEVICES.INDEX },
          ]}
        />
        <UDButton className="ml-6 flex items-center" variant="primary" onClick={openCreateModal}>
          <Plus size={16} className="mr-2" color={theme.colors.DarkBlue} />
          <span>Создать устройство</span>
        </UDButton>
      </div>
    ),
    [openCreateModal],
  );

  useEffect(() => {
    setHeaderContent(headerContent);
    return () => setHeaderContent(null);
  }, [headerContent, setHeaderContent]);

  return (
    <S.MainBlock>
      <div className="mb-4 flex items-center justify-between">
        <UDTippy
          content="В ссылку добавится параметр, благодаря которому, табло откроется сразу без боковой панели управления. После изменения, ссылку необходимо скопировать ещё раз."
          placement="top"
        >
          <div className="flex items-center gap-2">
            <UDCheckbox checked={hidePanel} onChange={(event) => setHidePanel(event.target.checked)} />
            <UDText variant="body-3" className="cursor-pointer select-none" onClick={toggleHidePanel}>
              Копировать ссылку на табло без панели управления
            </UDText>
          </div>
        </UDTippy>
        <UDPerPage value={perPage} onChange={setPerPage} />
      </div>

      <div>
        <UDTable isLoading={isLoading}>
          <thead>
            <tr>
              <th className="w-full">Название</th>
              <th className="w-[180px] min-w-[180px]">Дата создания</th>
              <th className="w-[36px] min-w-[36px]" />
              <th className="w-[36px] min-w-[36px]" />
              <th className="w-[36px] min-w-[36px]" />
              <th className="w-[36px] min-w-[36px]" />
            </tr>
          </thead>
          <tbody>
            {!isLoading && isError && (
              <tr>
                <td colSpan={6}>
                  <UDText variant="body-3" className="py-4 text-center">
                    Не удалось загрузить устройства
                  </UDText>
                </td>
              </tr>
            )}
            {!isLoading && !isError && devices.length === 0 && (
              <tr>
                <td colSpan={6}>
                  <div className="flex h-[300px] w-full flex-col items-center justify-center gap-2">
                    <img src={ImgNotFound} alt="" className="max-h-[200px] opacity-80" />
                    <span className="text-WhiteGray text-sm">Нет устройств</span>
                    <UDButton variant="primary" onClick={openCreateModal} className="mt-2">
                      Создать устройство
                    </UDButton>
                  </div>
                </td>
              </tr>
            )}
            {!isLoading &&
              !isError &&
              devices.map((device) => (
                <tr key={device.id}>
                  <td>{device.name}</td>
                  <td>{getNormalizedDate(device.created_at)}</td>
                  <td>
                    <UDTippy content="Редактировать" placement="top">
                      <EmptyButton icon={<Pencil size={20} />} className="w-full" onClick={() => openEditModal(device)} />
                    </UDTippy>
                  </td>
                  <td>
                    <UDTippy content="Копировать ссылку на табло" placement="top">
                      <EmptyButton icon={<Link size={20} />} className="w-full" onClick={() => handleCopyLink(device)} />
                    </UDTippy>
                  </td>
                  <td>
                    <UDTippy content="Копировать ссылку на табло для телевизора" placement="top">
                      <EmptyButton icon={<Tv size={20} />} className="w-full" onClick={() => handleCopyControlPanelLink(device)} />
                    </UDTippy>
                  </td>
                  <td>
                    <UDTippy content="Удалить" placement="top">
                      <EmptyButton
                        color={theme.colors.error}
                        icon={<Trash2 size={20} />}
                        className="w-full"
                        onClick={() => openDeleteModal(device)}
                      />
                    </UDTippy>
                  </td>
                </tr>
              ))}
          </tbody>
        </UDTable>
      </div>

      <div className="my-4 flex">
        <UDPagination pages={pages} currentPage={page} onPageChange={setPage} disabled={isLoading} />
      </div>

      <Modal show={isOpenForm} onClose={closeForm} size="md">
        <UDText as="h2" variant="title-3" className="mb-7.5">
          {selectedDevice ? 'Редактирование устройства' : 'Создание устройства'}
        </UDText>
        <DeviceForm initValue={selectedDevice} onSubmit={onSubmit} />
      </Modal>

      <Modal show={isOpenOverlayNotice} onClose={closeOverlayNotice} size="md">
        <Modal.Header title="Ссылка на табло перенесена" />

        <Modal.Body>
          <UDText variant="body-3" className="center mb-2 text-balance">
            Ссылка на&nbsp;табло больше не&nbsp;привязана к&nbsp;матчу&nbsp;— теперь она принадлежит устройству.
          </UDText>
          <UDText variant="body-3" className="center mb-2 text-balance">
            Скопируйте ссылку нужного устройства в&nbsp;этой таблице и&nbsp;вставьте её в&nbsp;OBS один раз: дальше она&nbsp;сама
            подхватит матч, который вы&nbsp;запустите.
          </UDText>
          <UDText variant="body-3" className="center mb-2 text-balance">
            Запуск конкретного матча и&nbsp;выбор устройства выполняется в&nbsp;мобильном приложении&nbsp;ImGame.
          </UDText>
          <UDText variant="body-3" className="center text-balance">
            Старый формат ссылок больше не&nbsp;поддерживается.
          </UDText>
        </Modal.Body>

        <Modal.Footer justify={'center'}>
          <UDButton variant="primary" onClick={closeOverlayNotice} stretch>
            Понятно
          </UDButton>
        </Modal.Footer>
      </Modal>

      <Modal.Confirm
        show={isOpenDelete}
        title={`Удалить устройство «${selectedDevice?.name ?? ''}»?`}
        description="Это действие необратимо."
        isLoading={deleteDevice.isLoading}
        onConfirm={onDelete}
        onCancel={closeDelete}
      />
    </S.MainBlock>
  );
}

export default DevicesPage;
